// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import api from './advisorApi';
import type {
  CreateTagRequest,
  EntityTagsDTO,
  MergeTagResultDTO,
  TagDTO,
  TagUsageDTO,
  TaggableEntityType,
  UpdateTagRequest,
} from '../types/tag';

/**
 * Resource-server tag taxonomy + assignment client (Tags PRD §8.1-8.2).
 * Every endpoint 404s when the `tags` feature flag is off for the tenant —
 * callers should treat a 404 as "hide all tag UI", not as an error. See
 * `useTagsFeature` / `useTags` in `../hooks/useTags.ts` for the caching +
 * feature-probe layer built on top of this module.
 */
export const tagsApi = {
  /** List tags. `withUsage=true` decorates each tag with its total assignment count (admin page). */
  list: async (params: { search?: string; includeArchived?: boolean; withUsage?: boolean } = {}): Promise<TagDTO[]> => {
    const response = await api.get<TagDTO[]>('/tags', {
      params: {
        search: params.search || undefined,
        includeArchived: params.includeArchived ?? undefined,
        withUsage: params.withUsage ?? undefined,
      },
    });
    return response.data;
  },

  create: async (data: CreateTagRequest): Promise<TagDTO> => {
    const response = await api.post<TagDTO>('/tags', data);
    return response.data;
  },

  update: async (id: string, data: UpdateTagRequest): Promise<TagDTO> => {
    const response = await api.put<TagDTO>(`/tags/${id}`, data);
    return response.data;
  },

  /** Merge this tag into `targetTagId`; the path tag (source) is deleted after re-pointing assignments. */
  merge: async (id: string, targetTagId: string): Promise<MergeTagResultDTO> => {
    const response = await api.post<MergeTagResultDTO>(`/tags/${id}/merge`, { targetTagId });
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await api.delete(`/tags/${id}`);
  },

  /** Per-entity-type assignment counts for a tag — pre-delete/merge confirm dialog data. */
  usage: async (id: string): Promise<TagUsageDTO> => {
    const response = await api.get<TagUsageDTO>(`/tags/${id}/usage`);
    return response.data;
  },

  /** Batch: tags per entity id — one call decorates a whole list page. */
  getForEntities: async (
    entityType: TaggableEntityType,
    entityIds: string[],
  ): Promise<Record<string, TagDTO[]>> => {
    if (entityIds.length === 0) return {};
    const response = await api.get<Record<string, TagDTO[]>>('/tags/assignments', {
      params: { entityType, entityIds },
      paramsSerializer: {
        indexes: null, // entityIds=a&entityIds=b (repeatable, not entityIds[]=a)
      },
    });
    return response.data;
  },

  /** Replace an entity's full tag set — set semantics, computes the add/remove delta server-side. */
  replace: async (
    entityType: TaggableEntityType,
    entityId: string,
    tagIds: string[],
  ): Promise<EntityTagsDTO> => {
    const response = await api.put<EntityTagsDTO>(
      `/tags/assignments/${entityType}/${entityId}`,
      { tagIds },
    );
    return response.data;
  },

  assign: async (entityType: TaggableEntityType, entityId: string, tagId: string): Promise<EntityTagsDTO> => {
    const response = await api.post<EntityTagsDTO>(`/tags/assignments/${entityType}/${entityId}/${tagId}`);
    return response.data;
  },

  unassign: async (entityType: TaggableEntityType, entityId: string, tagId: string): Promise<EntityTagsDTO> => {
    const response = await api.delete<EntityTagsDTO>(`/tags/assignments/${entityType}/${entityId}/${tagId}`);
    return response.data;
  },
};

export default tagsApi;
